import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Central color-swatch mapping used by ProductCard, ProductDetail, and Shop filter.
 * Merges: 1) hard-coded built-ins (below, always available)
 *         2) admin-managed hex codes from GET /api/color-swatches (loaded on mount)
 *
 * Lookup is case-insensitive on the color NAME.
 */

// Built-in fallbacks for common industry-standard color names.
const BUILT_IN_SWATCHES = {
  gold:       "linear-gradient(135deg,#F3E5AB,#D4AF37,#996515)",
  "rose-gold":"linear-gradient(135deg,#F5D0C5,#B76E79)",
  rose:       "linear-gradient(135deg,#F5D0C5,#B76E79)",
  silver:     "linear-gradient(135deg,#EFEFEF,#B0B0B0,#7A7A7A)",
  platinum:   "linear-gradient(135deg,#EAEAEA,#9EA0A2)",
  black:      "#1C1C1C",
  white:      "#FFFFFF",
  ivory:      "#FFF8E7",
  pearl:      "linear-gradient(135deg,#FFFFFF,#F0EAD6)",
  copper:     "linear-gradient(135deg,#F1B182,#B87333)",
  bronze:     "linear-gradient(135deg,#F1B182,#8C5A2D)",
  green:      "#3F7A56",
  emerald:    "#046A38",
  ruby:       "#B4123E",
  red:        "#B4123E",
  blue:       "#2D5EA3",
  navy:       "#1F3A68",
  turquoise:  "#40C0C0",
};

// Module-level cache — populated once per page load, then reused by any subsequent
// hook call so the swatch API isn't re-hit for every ProductCard.
let _cache = null;
let _loading = null;

async function _load() {
  if (_cache) return _cache;
  if (_loading) return _loading;
  _loading = api.get("/color-swatches")
    .then(({ data }) => {
      const map = { ...BUILT_IN_SWATCHES };
      (data || []).forEach((s) => {
        if (s && s.name && s.hex) map[s.name.toLowerCase()] = s.hex;
      });
      _cache = map;
      return map;
    })
    .catch(() => {
      _cache = { ...BUILT_IN_SWATCHES };
      return _cache;
    });
  return _loading;
}

/** React hook — returns the current merged swatch map (built-ins + admin overrides). */
export function useColorSwatches() {
  const [map, setMap] = useState(_cache || BUILT_IN_SWATCHES);
  useEffect(() => {
    let alive = true;
    _load().then((m) => { if (alive) setMap(m); });
    return () => { alive = false; };
  }, []);
  return map;
}

/** Compute the inline CSS style object for a swatch of the given color name. */
export function swatchStyle(colorName, map) {
  const key = (colorName || "").toLowerCase().trim();
  const val = (map || BUILT_IN_SWATCHES)[key];
  if (val) return val.startsWith("linear-gradient") ? { backgroundImage: val } : { background: val };
  // Last-resort fallback — pass raw string to CSS (may or may not resolve to a color).
  return { background: key || "#EEE" };
}
